import { CaretDownFilled, SettingOutlined, CloseCircleFilled } from '@ant-design/icons'
import { unwrapResult } from '@reduxjs/toolkit'
import {
  Col,
  Dropdown,
  Form,
  FormProps,
  Input,
  Menu,
  Radio,
  Row,
  Select,
  Cascader as Cascaded,
} from 'antd'
import moment from 'moment'
import React, { ReactElement, useContext, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RootDispatch } from '../../../app/store'
import { getMaritalStatusName, MaritalStatus, maritalStatusCategories } from '../../../models/patient'
import { Gender, GenderOptions, getGenderName } from '../../../models/user'
import { ThemeContext } from '../../../theme/ThemeContext'
import { getAddress } from '../../registration/register/registerSlice'
import styles from './PatientForm.module.css'
import { getPatientOptionsAsync } from '../../A-toothModule/toothHome/toothHomeSlice'
import { SettingOptionsModal } from '../../A-toothModule/toothHome/modal/settingOptionsModal'
import { phonePatterns } from '../../../utils/RegExp'
import { selectEdition } from '../../../app/applicationSlice'
import { getFieldSettingList } from '../../FunctionSetting/functionSettingPage/fieldSetting/FieldSettingSlice'
import {NATION_LIST} from "../../../models/nation";

interface RegistrationsProps {
  registrations?: any
  form?: any
  CKHZSJHData?: any
  CKHZSFZHData?: any 
  patients?: any
}

export const PatientForm = ({
  registrations,
  form,
  CKHZSJHData,
  CKHZSFZHData,
  patients,
  ...others
}: RegistrationsProps & FormProps): ReactElement => {
  const hisVersion = useSelector(selectEdition)

  const dispatch = useDispatch<RootDispatch>()

  const theme = useContext(ThemeContext)

  const [ageUnit, setAgeUnit] = useState<'year' | 'month'>('year')

  const [fieldSettingList, setFieldSettingList] = useState<any[]>([]) //字段必填设置

  const [maskInfo, setMaskInfo] = useState<any>({})

  useEffect(() => {
      setMaskInfo({
        phone: patients?.phone,
        emergencyContactPhone: patients?.emergencyContactPhone,
        idCardNumber: patients?.idCardNumber,
        phoneDisabled: patients?.phone && !CKHZSJHData,
        emergencyContactPhoneDisabled: patients?.emergencyContactPhone && !CKHZSJHData,
        idCardNumberDisabled: patients?.idCardNumber && !CKHZSFZHData,
        phoneHover: false,
        emergencyContactPhoneHover: false,
      })
  }, [patients,CKHZSJHData,CKHZSFZHData])

  // 编辑回填时，根据 birthday 自动判断使用「年-月」还是「月-天」
  useEffect(() => {
    if (patients) {
      const birthday = patients.birthday
      if (birthday && moment(birthday).isValid()) {
        const date = moment(birthday)
        if (date.isAfter(moment(), 'day')) return
        const totalMonths = moment().diff(date, 'months')
        if (totalMonths >= 12) {
          setAgeUnit('year')
        } else {
          setAgeUnit('month')
        }
      }
      // 回填时，已有身份证号需设置禁用状态
      if (patients.idCardNumber) {
        getIdCardInfo(patients.idCardNumber)
      }
    }
  }, [patients])

  useEffect(() => {
    dispatch(getFieldSettingList({
      functionType: 2,
      templateType: hisVersion == 1 ? 'KQHZSZ' : 'HZSZ',
    }))
      .then(unwrapResult)
      .then((res: any) => {
        const data = res?.[0]?.fields || []
        setFieldSettingList(data);
      })
  }, [])

  const layouts = {
    labelCol: { span: 2 },
    wrapperCol: { span: 22 },
  }

  const layoutSecond = {
    labelCol: { span: 3 },
    wrapperCol: { span: 21 },
  }
  const [idCardRules, setIdCardRules] = useState<any[]>([
    {
      pattern: /(^\d{15}$)|(^\d{16}$)|(^\d{18}$)|(^\d{17}(\d|X|x)$)/,
      message: '你的身份证格式不正确',
    },
    {
      max: 18,
      message: '最长18位!',
    },
  ])
  const [idCardInputValid, setIdCardInputValid] = useState(false)

  const getIdCardInfo = (idCard: string) => {
    if (idCard) {
      //先判断是不是中国大陆身份证
      if (
        (idCard.length != 15 && idCard.length != 16 && idCard.length != 18) ||
        isNaN(Number(idCard.substr(0, 6)))
      ) {
        setIdCardInputValid(false)
        setIdCardRules([
          {
            max: 18,
            message: '最长18位!',
          },
        ])
      } else {
        setIdCardInputValid(true)
        setIdCardRules([
          {
            pattern: /(^\d{15}$)|(^\d{16}$)|(^\d{18}$)|(^\d{17}(\d|X|x)$)/,
            message: '身份证格式错误',
          },
          {
            max: 18,
            message: '最长18位!',
          },
        ])
        // 计算性别
        const sex = Number(idCard?.[idCard?.length - 2]) % 2 == 0 ? 2 : 1
        // 计算生日
        let birthday = ''
        let year = 0
        let month = 0
        let day = 0
        if (idCard.length === 18) {
          year = parseInt(idCard.substr(6, 4))
          month = parseInt(idCard.substr(10, 2))
          day = parseInt(idCard.substr(12, 2))
          birthday = moment(idCard.substr(6, 8)).format('YYYY/MM/DD')
        } else {
          year = parseInt('19' + idCard.substr(6, 2))
          month = parseInt(idCard.substr(8, 2))
          day = parseInt(idCard.substr(10, 2))
          birthday = moment('19' + idCard.substr(6, 6)).format('YYYY/MM/DD')
        }
        // 计算年龄
        const now = new Date()
        const getMonth = now.getMonth() + 1
        const getDay = now.getDate()
        const getFullYear = now.getFullYear()
        let ageYear = 0
        let ageMonth = 0
        if (getMonth - month > 0) {
          ageYear = getFullYear - year
          ageMonth = getMonth - month
          if (getDay < day) ageMonth = Math.max(0, ageMonth - 1)
        } else if (getMonth - month < 0) {
          ageYear = getFullYear - year - 1
          ageMonth = 12 - (month - getMonth)
          if (getDay < day) ageMonth = Math.max(0, ageMonth - 1)
        } else {
          if (getDay < day) {
            ageYear = getFullYear - year - 1
            ageMonth = 11
          } else {
            ageYear = getFullYear - year
            ageMonth = 0
          }
        }
        const totalMonths = ageYear * 12 + ageMonth
        if (totalMonths < 12) {
          setAgeUnit('month')
          // 计算剩余天数
          const birthdayMoment = moment(birthday)
          let ageDayValue = moment().diff(
            birthdayMoment.clone().add(totalMonths, 'months'),
            'days'
          )
          if (ageDayValue < 0 && totalMonths > 0) {
            ageDayValue = moment().diff(
              birthdayMoment.clone().add(totalMonths - 1, 'months'),
              'days'
            )
            form.setFieldsValue({
              ageMonth: totalMonths - 1 <= 0 ? 0 : totalMonths - 1,
              ageDay: ageDayValue,
              sex,
              birthday,
            })
          } else {
            form.setFieldsValue({
              ageMonth: totalMonths <= 0 ? 0 : totalMonths,
              ageDay: ageDayValue,
              sex,
              birthday,
            })
          }
        } else {
          setAgeUnit('year')
          form.setFieldsValue({
            ageYear: ageYear < 0 ? 1 : ageYear,
            ageMonth: ageMonth <= 0 ? 0 : ageMonth,
            sex,
            birthday,
          })
        }
      }
    } else {
      setIdCardInputValid(false)
    }
  }

  const updateBirthday = () => {
    if (form) {
      if (ageUnit === 'year') {
        const year = parseInt(form.getFieldValue('ageYear')) || 0
        const month = parseInt(form.getFieldValue('ageMonth')) || 0
        if (year !== 0 || month !== 0) {
          const months = year * 12 + month
          const birthday = moment()
            .subtract(months, 'months')
            .format('YYYY/MM/DD')
          form.setFieldsValue({ birthday })
        }
      } else {
        const month = parseInt(form.getFieldValue('ageMonth')) || 0
        const day = parseInt(form.getFieldValue('ageDay')) || 0
        if (month !== 0 || day !== 0) {
          const birthday = moment().subtract(month, 'months').subtract(day, 'days').format('YYYY/MM/DD')
          form.setFieldsValue({ birthday })
        }
      }
    }
  }

  const updateYearsAndMonths = () => {
    if (form) {
      const date = moment(form.getFieldValue('birthday'))
      if (!date.isValid()) {
        return
      }
      if (date.isAfter(moment(), 'day')) {
        return
      }
      const totalMonths = moment().diff(date, 'months')
      if (totalMonths >= 12) {
        const year = Math.floor(totalMonths / 12)
        const month = totalMonths % 12
        setAgeUnit('year')
        form.setFieldsValue({
          ageYear: year,
          ageMonth: month,
        })
      } else {
        const days = moment().diff(date.clone().add(totalMonths, 'months'), 'days')
        setAgeUnit('month')
        form.setFieldsValue({
          ageMonth: totalMonths,
          ageDay: days,
        })
      }
    }
  }

  const [allList, setAllList] = useState<any[]>() //省市数据

  const [classificationList, setClassificationList] = useState<any>() //自定义患者来源数据
  
  const [settingOptionsVisible, setSettingOptionsVisible] = useState(false) //自定义选项

  const [settingType, setSettingType] = useState('0') // 自定义类型

  useEffect(() => {
    //获取省市数据
    dispatch(getAddress())
      .then(unwrapResult)
      .then((res: any) => {
        setAllList(res)
      })
  }, [])

  useEffect(() => {
    if(!settingOptionsVisible) {
      dispatch(getPatientOptionsAsync({ types: '0,1,2' }))
        .then(unwrapResult)
        .then((res: any) => {
          const findItems = (type: string) => {
            return res ? res?.find((t: any) => t.type == type)?.items?.map((v: any) => {
              return {
                name: v.context,
                value: v.sortNo,
              }
            }) : []
          }
          setClassificationList({
            sourceList: findItems('1'),
          })
        })
    }
  }, [settingOptionsVisible])

  return (
    <Form
      form={form}
      className={styles.form}
      autoComplete='off'
      colon={false}
      initialValues={{
        sex: Gender.Male,
      }}
      {...others}
      labelCol={{ span: 6 }}
      wrapperCol={{ span: 18 }}
    >
      <Row>
        <Col span={8} className={styles.fst}>
          <Form.Item
            name='name'
            label='患者姓名'
            rules={[{ required: true }, { max: 40, message: '最长40位' }]}
          >
            <Input
              disabled={
                registrations?.insuranceCode &&
                registrations?.insuranceCode != 'ZIFEI'
                  ? true
                  : false
              }
            />
          </Form.Item>
        </Col>
        <Col span={8} className={styles.sex}>
          <Form.Item name='sex' label='性别' rules={[{ required: true }]}>
            <Radio.Group
              disabled={
                (registrations?.insuranceCode &&
                registrations?.insuranceCode != 'ZIFEI'
                  ? true
                  : false) || idCardInputValid
              }
            >
              {GenderOptions.map((g) => (
                <Radio key={g} value={g}>
                  {getGenderName(g)}
                </Radio>
              ))}
            </Radio.Group>
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            label='年龄'
            style={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'nowrap',
            }}
            required
            className={styles.snd}
          >
            <Row wrap={false} align='middle'>
              <Form.Item
                name={ageUnit === 'month' ? 'ageMonth' : 'ageYear'}
                noStyle
                dependencies={ageUnit === 'month' ? ['ageDay'] : ['ageMonth']}
                rules={[
                    {
                      pattern: ageUnit === 'month' ? /^(1[01]|[0-9])$/ : /^(200|[01]?[0-9]{1,2})$/,
                      message: ageUnit === 'month' ? '请输入正确的月份' : '请输入正确的年龄',
                    },
                  ({ getFieldValue }) => ({
                    validator: (_, value) => {
                      const secondField = ageUnit === 'month' ? getFieldValue('ageDay') : getFieldValue('ageMonth')
                      if ((!value && value !== 0) && (!secondField && secondField !== 0)) {
                        return Promise.reject(new Error('请输入正确的年龄'))
                      }
                      return Promise.resolve()
                    },
                  }),
                ]}
              >
                <Input
                  disabled={
                    (registrations?.insuranceCode &&
                    registrations?.insuranceCode != 'ZIFEI'
                      ? true
                      : false) || idCardInputValid
                  }
                  style={{ width: '5.5rem', textAlign: 'center' }}
                  onChange={updateBirthday}
                />
              </Form.Item>
              <Dropdown
                disabled={idCardInputValid}
                trigger={['click']}
                overlay={
                  <Menu
                    selectedKeys={[ageUnit]}
                    onClick={({ key }) => {
                      if (key !== ageUnit) {
                        const newUnit = key as 'year' | 'month'
                        const rawFirst = form.getFieldValue(ageUnit === 'month' ? 'ageMonth' : 'ageYear')
                        const rawSecond = form.getFieldValue(ageUnit === 'month' ? 'ageDay' : 'ageMonth')
                        if (newUnit === 'month') {
                          form.setFieldsValue({ ageMonth: rawFirst, ageDay: rawSecond })
                          setAgeUnit('month')
                          const months = parseInt(rawFirst) || 0
                          const days = parseInt(rawSecond) || 0
                          if (months || days) {
                            const birthday = moment().subtract(months, 'months').subtract(days, 'days').format('YYYY/MM/DD')
                            form.setFieldsValue({ birthday })
                          }
                          setTimeout(() => {
                            if (rawFirst || rawSecond) {
                              form.validateFields(['ageMonth', 'ageDay'])
                            }
                          })
                        } else {
                          form.setFieldsValue({ ageYear: rawFirst, ageMonth: rawSecond })
                          setAgeUnit('year')
                          const years = parseInt(rawFirst) || 0
                          const months = parseInt(rawSecond) || 0
                          if (years || months) {
                            const birthday = moment().subtract(years, 'years').subtract(months, 'months').format('YYYY/MM/DD')
                            form.setFieldsValue({ birthday })
                          }
                          setTimeout(() => {
                            if (rawFirst || rawSecond) {
                              form.validateFields(['ageYear', 'ageMonth'])
                            }
                          })
                        }
                      }
                    }}
                    style={{ minWidth: 50 }}
                  >
                    <Menu.Item key="year">岁</Menu.Item>
                    <Menu.Item key="month">月</Menu.Item>
                  </Menu>
                }
              >
                <div
                  style={{
                    color: theme.tc2,
                    fontSize: '1rem',
                    margin: '0 6px',
                    cursor: 'pointer',
                    userSelect: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                  }}
                >
                  {ageUnit === 'year' ? '岁' : '月'}
                  <CaretDownFilled style={{ fontSize: '0.7rem', marginLeft: 2 }} />
                </div>
              </Dropdown>
              <Form.Item
                name={ageUnit === 'month' ? 'ageDay' : 'ageMonth'}
                noStyle
                rules={[
                    {
                      pattern: ageUnit === 'month' ? /^(30|[012]?[0-9])$/ : /^(1[01]|[0-9])$/,
                      message: ageUnit === 'month' ? '请输入正确的天数' : '请输入正确的月份',
                    },
                  ]}
              >
                <Input
                  disabled={
                    (registrations?.insuranceCode &&
                    registrations?.insuranceCode != 'ZIFEI'
                      ? true
                      : false) || idCardInputValid
                  }
                  style={{ width: '5.5rem', textAlign: 'center' }}
                  onChange={updateBirthday}
                />
              </Form.Item>
              <div
                style={{
                  color: theme.tc2,
                  fontSize: '1rem',
                  margin: '0 6px',
                }}
              >
                {ageUnit === 'year' ? '月' : '天'}
              </div>
              <Form.Item
                name='birthday'
                noStyle style={{ flex: 1 }}
                rules={[
                  { max: 10, message: '最多10个字' },
                  () => ({
                    validator(_, value) {
                      if (!value) return Promise.resolve()
                      const str = String(value)
                      if (!/^(?:1\d{3}|2\d{3})-(?:0[1-9]|1[0-2])-(?:0[1-9]|[12]\d|3[01])|(?:1\d{3}|2\d{3})\/(?:0[1-9]|1[0-2])\/(?:0[1-9]|[12]\d|3[01])|(?:1\d{3}|2\d{3})(?:0[1-9]|1[0-2])(?:0[1-9]|[12]\d|3[01])$/.test(str)) {
                        return Promise.reject(new Error('格式参考:19980808'))
                      }
                      const digits = str.replace(/[-/]/g, '')
                      const now = new Date()
                      const todayStr = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`
                      if (digits > todayStr) {
                        form.setFieldsValue({
                          ageYear: '',
                          age: '',
                          ageMonth: '',
                          ageDay: '',
                        })
                        return Promise.reject(new Error('出生日期填写不对'))
                      }
                      return Promise.resolve()
                    },
                  }),
                ]}>
                <Input
                  onChange={updateYearsAndMonths}
                  disabled={
                    idCardInputValid ||
                    (registrations?.insuranceCode &&
                    registrations?.insuranceCode != 'ZIFEI'
                      ? true
                      : false)
                  }
                />
              </Form.Item>
            </Row>
          </Form.Item>
        </Col>
        <Col span={8} className={styles.fst}>
          <Form.Item
            label='身高'
            name='height'
            rules={[
              { required: fieldSettingList?.find((item) => item.key == 'height')?.isRequired ,
                message: "请输入身高"
              },
              {
                message: "请输入最多带两位小数的数值",
                pattern: new RegExp(/^[0-9]+((\.)[0-9]{0,2})?$/),
              },
            ]}
          >
            <Input suffix='cm' />
          </Form.Item>
        </Col>
        <Col span={8} className={styles.fst}>
          <Form.Item
            label='体重'
            name='weight'
            rules={[
              { required: fieldSettingList?.find((item) => item.key == 'weight')?.isRequired ,
                message: "请输入体重"
              },
              {
                message: '请输入最多带两位小数的数值',
                pattern: new RegExp(/^[0-9]+((\.)[0-9]{0,2})?$/),
              },
            ]}
          >
            <Input suffix='kg' />
          </Form.Item>
        </Col>
        <Col span={8} className={styles.snd}>
          <Form.Item label='腰围' name='waistline' 
            rules={[
              { required: fieldSettingList?.find((item) => item.key == 'waistline')?.isRequired ,
                message: "请输入腰围"
              },
              {
                message: '请输入最多带两位小数的数值',
                pattern: new RegExp(/^[0-9]+((\.)[0-9]{0,2})?$/),
              },
            ]}>
            <Input suffix='cm'/>
          </Form.Item>
        </Col>
        <Col span={8} className={styles.fst}>
          <Form.Item
            label='婚姻'
            name='maritalStatus'
            rules={[
              { required: fieldSettingList?.find((item) => item.key == 'maritalStatus')?.isRequired ,
                message: "请选择婚姻状况"
              },
            ]}
          >
            <Select allowClear={true}>
              {maritalStatusCategories?.map((s: any) => (
                <Select.Option key={s} value={s}>
                  {getMaritalStatusName(s)}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={8} className={styles.fst}>
          <Form.Item
            label='邮箱地址'
            name='emailAddress'
            rules={[
              { required: fieldSettingList?.find((item) => item.key == 'emailAddress')?.isRequired ,
                message: "请输入邮箱地址"
              },
              {
                max: 50,
                message: '最长50位!',
              },
            ]}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col span={8} className={styles.snd}>
          <Form.Item
            label="微信号"
            name="wechat"
            rules={[
              {
                required: fieldSettingList?.find((item) => item.key == 'wechat')?.isRequired,
                message: "请输入微信号"
              },
            ]}>
            <Input />
          </Form.Item>
        </Col>
        <Col span={8} className={styles.fst}>
          <Form.Item
            name='phone'
            label='联系方式'
            rules={[
              maskInfo?.phoneDisabled ? {} : { ...phonePatterns },
              {
                required: fieldSettingList?.find((item) => item.key == 'phone')?.isRequired,
                message: "请输入联系方式"
              },
            ]}
          >
            <Input 
              maxLength={11} 
              disabled={maskInfo?.phoneDisabled} 
              suffix={
                maskInfo?.phoneDisabled ? (
                  <CloseCircleFilled 
                    onClick={() => {
                      form.setFieldsValue({ phone: '' })
                      setMaskInfo({
                        ...maskInfo,
                        phone: '',
                        phoneDisabled: false,
                      })
                    }} 
                    style={{ color: '#999', cursor: 'pointer' }} 
                  />
                ) : null
              }
            />
          </Form.Item>
        </Col>
        <Col span={8} className={styles.fst}>
          <Form.Item
            name="idCardNumber"
            label="身份证号"
            rules={[
              ...(maskInfo?.idCardNumberDisabled ? [{
                max: 18,
                message: '最长18位!',
              },] : idCardRules),
              {
                required: fieldSettingList?.find((item: any) => item.key == 'idCardNumber')?.isRequired,
                message: "请输入身份证号"
              },
            ]}
          >
            <Input
              disabled={
                maskInfo?.idCardNumberDisabled || registrations?.insuranceCode &&
                registrations?.insuranceCode != 'ZIFEI'
                  ? true
                  : false
              }
              onBlur={() => {
                // 失焦后再做证件合法性判断与年龄/性别/生日派生计算，避免输入过程中的非法中间值
                getIdCardInfo(form.getFieldValue('idCardNumber'))
              }}
              suffix={
                maskInfo?.idCardNumberDisabled ? (
                  <CloseCircleFilled 
                    onClick={() => {
                      form.setFieldsValue({ idCardNumber: '' })
                      setMaskInfo({
                        ...maskInfo,
                        idCardNumber: '',
                        idCardNumberDisabled: false,
                      })
                    }} 
                    style={{ color: '#999', cursor: 'pointer' }} 
                  />
                ) : null
              }
            />
          </Form.Item>
        </Col>
        <Col span={8} className={styles.snd}>
          <Form.Item
            name='companyName'
            label='工作单位'
            rules={[
              {
                required: fieldSettingList?.find((item) => item.key == 'companyName')?.isRequired,
                message: "请输入工作单位"
              },
              {
                max: 120,
                message: '最长120位!',
              },
            ]}
          >
            <Input
              maxLength={120}
              disabled={
                registrations?.insuranceCode &&
                registrations?.insuranceCode != 'ZIFEI'
                  ? true
                  : false
              }
            />
          </Form.Item>
        </Col>
        <Col span={8} className={styles.fst}>
          <Form.Item
            name="area"
            label="家庭地区"
            rules={[
              {
                required: fieldSettingList?.find((item) => item.key == 'area')?.isRequired,
                message: "请选择家庭地区"
              },
            ]}
          >
            <Cascaded
              allowClear
              // loadData={loadData}
              options={allList}
              placeholder='请选择'
            />
          </Form.Item>
        </Col>
        <Col span={16} className={styles.fst}>
          <Form.Item
            {...layoutSecond}
            name='address'
            label='详细地址'
            rules={[
              {
                required: fieldSettingList?.find((item) => item.key == 'address')?.isRequired,
                message: "请输入详细地址"
              },
              {
                max: 250,
                message: '最长250位!',
              },
            ]}
          >
            <Input />
          </Form.Item>
        </Col>
        {/* <Col span={24} className={styles.fst}>
          <Form.Item
            name='address'
            label='地址'
            rules={[
              {
                max: 250,
                message: '最长250位!',
              },
            ]}
          >
            <Input />
          </Form.Item>
        </Col> */}
        <Col span={8} className={styles.fst}>
          <Form.Item
            name='emergencyContactName'
            label='紧急联系人'
            rules={[
              {
                required: fieldSettingList?.find((item) => item.key == 'emergencyContactName')?.isRequired,
                message: "请输入紧急联系人"
              },
              {
                max: 40,
                message: '最长40位!',
              },
            ]}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col span={8} className={styles.fst}>
          <Form.Item
            name='emergencyContactPhone'
            label='紧急联系方式'
            rules={[
                maskInfo?.emergencyContactPhoneDisabled ? {} : { ...phonePatterns },
              {
                required: fieldSettingList?.find((item) => item.key == 'emergencyContactPhone')?.isRequired,
                message: "请输入紧急联系方式"
              },
              {
                max: 40,
                message: '最长40位!',
              },
            ]}
          >
            <Input 
              disabled={maskInfo?.emergencyContactPhoneDisabled} 
              suffix={
                maskInfo?.emergencyContactPhoneDisabled ? (
                  <CloseCircleFilled 
                    onClick={() => {
                      form.setFieldsValue({ emergencyContactPhone: '' })
                      setMaskInfo({
                        ...maskInfo,
                        emergencyContactPhone: '',
                        emergencyContactPhoneDisabled: false,
                      })
                    }} 
                    style={{ color: '#999', cursor: 'pointer' }} 
                  />
                ) : null
              }
            />
          </Form.Item>
        </Col>
        <Col span={8} className={styles.snd}>
          <Form.Item
            label="患者来源"
            name="patientSource"
            rules={[
              {
                required: fieldSettingList?.find((item) => item.key == 'patientSource')?.isRequired,
                message: "请选择患者来源"
              },
            ]}
          >
            <Select>
              {classificationList?.sourceList.map((v: any) => {
                return (
                  <Select.Option key={v.name} value={v.name}>
                    {v.name}
                  </Select.Option>
                )
              })}
            </Select>
          </Form.Item>
          <div style={{ position: 'absolute', top: '9px', right: '-20px', zIndex: 999 }}>
            <SettingOutlined
              style={{ color: '#999999', fontSize: '16px' }}
              onClick={() => {
                setSettingOptionsVisible(true)
                setSettingType('1')
              }}
            />
          </div>
        </Col>
        <Col span={8} className={styles.fst}>
          <Form.Item
            name='nativePlace'
            label='籍贯'
            rules={[
              {
                required: fieldSettingList?.find((item) => item.key == 'nativePlace')?.isRequired,
                message: "请输入籍贯"
              },
              {
                max: 40,
                message: '最长40位!',
              },
            ]}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col span={8} className={styles.snd}>
          <Form.Item
            label="民族"
            name="nation"
            rules={[
              {
                required: fieldSettingList?.find((item) => item.key == 'nation')?.isRequired,
                message: "请选择民族"
              },
            ]}
          >
              <Select
                  allowClear
                  placeholder='请选择民族'
              >
                  {NATION_LIST.map((nation) => (
                      <Select.Option key={nation} value={nation}>
                          {nation}
                      </Select.Option>
                  ))}
              </Select>
          </Form.Item>
        </Col>
        <Col span={8} className={styles.snd}>
          <Form.Item
            label='职业'
            name='job'
            rules={[
              {
                required: fieldSettingList?.find((item) => item.key == 'job')?.isRequired,
                message: "请输入职业"
              },
            ]}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col span={24} className={styles.fst}>
          <Form.Item
            {...layouts}
            name='remark'
            label='备注'
            rules={[
              {
                max: 250,
                message: '最长250位!',
              },
            ]}
          >
            <Input />
          </Form.Item>
        </Col>
      </Row>

      <SettingOptionsModal
        visible={settingOptionsVisible}
        type={settingType}
        onCancel={() => {
          setSettingOptionsVisible(false)
        }}
        onOk={() => {
          setSettingOptionsVisible(false)
        }}
      />

    </Form>
  )
}
